import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { MapPinIcon, Bars3Icon, MagnifyingGlassIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { projectAPI, thingsAPI } from '../services/api';
import ProjectCard from '../components/ProjectCard';
import ObservationCard from '../components/ObservationCard';
import ListMapView from '../components/ListMapView';
import ListPageContent from '../components/ListPageContent';
import MobileSearchOverlay from '../components/MobileSearchOverlay';
import SearchUI from '../components/SearchUI';
import { useListMapView } from '../hooks/useListMapView';
import { useListPageData } from '../hooks/useListPageData';
import { useListPageFilters } from '../hooks/useListPageFilters';
import { getImageUrl } from '../config/runtime';
import tagsData from '../data/tags.json';
import { getLocationFilterParams } from '../utils/locationFilters';
import { projectHasGeolocation } from '../utils/projectLocation';

// Result type options for the primary discover mode switcher
const RESULT_TYPE_OPTIONS = [
  { value: 'projects', label: 'Projects', icon: '📁' },
  { value: 'observations', label: 'Observations', icon: '🔍' },
];

// Observation category tags (from old CommunityPage)
const OBSERVATION_TAGS = [
  { id: 'nearby', label: 'Nearby', icon: '📍', description: 'Find what\'s around you' },
  { id: 'organism', label: 'Plants & Animals', icon: '🌿', description: 'Living things' },
  { id: 'fungus', label: 'Fungi', icon: '🍄', description: 'Mushrooms & fungi' },
  { id: 'artifact', label: 'Objects', icon: '🏺', description: 'Man-made items' },
  { id: 'environment', label: 'Places', icon: '🏞️', description: 'Landscapes & habitats' },
];

/**
 * DiscoverPage - Browse public projects and observations shared by the community
 * 
 * Uses the same modular component architecture as ProjectsPage and TemplatesPage:
 * - ListMapView for responsive list/map layout
 * - ListPageContent for the scrollable project grid
 * - useListMapView, useListPageData, useListPageFilters hooks
 * - ProjectCard or ObservationCard for consistent rendering
 * 
 * Supports two result types:
 * - "projects": Shows public projects (default)
 * - "observations": Shows detections/discoveries from ThingsDB
 */
const DiscoverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Result type state - read from URL or default to 'projects'
  const resultType = searchParams.get('type') || 'projects';
  const setResultType = useCallback((type) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (type === 'projects') {
        next.delete('type');
      } else {
        next.set('type', type);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Use the reusable list/map view hook
  const listMapState = useListMapView({
    enableMap: true,
    treatTabletAsMobile: true,
    autoEnableMapFilter: true,
  });

  const {
    isMobile,
    viewMode,
    shouldRenderMobileMap,
    isDesktopSplitView,
    userLocation,
    isMobileSearchOpen,
    setIsMobileSearchOpen,
    mobileSearchHeaderRef,
    handleDesktopViewChange,
    isFullyExpanded,
    mapViewportBbox,
  } = listMapState;

  // Get appropriate filter tags based on result type
  const allFilterTags = useMemo(() => {
    if (resultType === 'observations') {
      return OBSERVATION_TAGS;
    }
    return [
      { id: 'nearby', label: 'Nearby', icon: '📍', description: 'Find what\'s around you' },
      ...tagsData.tags
    ];
  }, [resultType]);

  // Use filter hook with search enabled
  const filters = useListPageFilters({
    enableSearch: true,
    enableTags: true,
    enableSort: false,
    enableStatus: false,
  });

  const {
    searchTerm,
    mobileSearchTerm,
    selectedTags,
    mapViewportCenter,
    mapUserInteracted,
    mapSyncEnabled,
    setSearchTerm,
    setMobileSearchTerm,
    setSelectedTags,
    setMapViewportBbox,
    setMapViewportCenter,
    setMapViewportRadius,
    setMapUserInteracted,
    setMapSyncEnabled,
    handleSearch,
    clearAllFilters,
    filters: filterValues,
  } = filters;

  // Clear tags when switching result types (since tag sets are different)
  useEffect(() => {
    setSelectedTags([]);
  }, [resultType, setSelectedTags]);

  const mapAreaFilterActive = Boolean(
    mapViewportCenter && mapUserInteracted && mapSyncEnabled
  );

  const viewportStateHandlersRef = useRef({
    handleClearMapAreaFilter: null,
    handleEnableMapAreaFilter: null,
  });

  const handleViewportStateChange = useCallback((viewportState) => {
    const isMapVisible = shouldRenderMobileMap || (!isMobile && viewMode === 'map');

    viewportStateHandlersRef.current = {
      handleClearMapAreaFilter: viewportState.handleClearMapAreaFilter,
      handleEnableMapAreaFilter: viewportState.handleEnableMapAreaFilter,
    };

    setMapSyncEnabled(viewportState.mapSyncEnabled);
    setMapUserInteracted(viewportState.mapUserInteracted);

    if (isMapVisible && viewportState.mapViewportCenter) {
      setMapViewportBbox(viewportState.mapViewportBbox);
      setMapViewportCenter(viewportState.mapViewportCenter);
      setMapViewportRadius(viewportState.mapViewportRadius);
    }
  }, [shouldRenderMobileMap, isMobile, viewMode, setMapViewportBbox, setMapViewportCenter, setMapViewportRadius, setMapUserInteracted, setMapSyncEnabled]);

  const handleClearMapAreaFilter = useCallback(() => {
    viewportStateHandlersRef.current.handleClearMapAreaFilter?.();
  }, []);

  // ============================================
  // PROJECTS DATA FETCHING
  // ============================================
  const buildProjectQueryParams = useCallback((filters, pageParam, limit) => {
    const params = {
      offset: pageParam,
      limit,
      strictPublicOnly: true,
    };

    if (filters.searchTerm) {
      params.search = filters.searchTerm;
    }

    const allTags = (filters.selectedTags || []).filter(tag => tag !== 'nearby');
    if (allTags.length > 0) {
      params.tags = allTags.join(',');
    }

    const locationParams = getLocationFilterParams(filters, userLocation);
    if (locationParams) {
      params.lat = locationParams.lat;
      params.lng = locationParams.lng;
      params.radius = locationParams.radius;
    }

    return params;
  }, [userLocation]);

  const transformProjectResponse = useCallback((response) => {
    return response?.data?.data?.projects || [];
  }, []);

  const projectsData = useListPageData({
    queryKey: 'discover-public-projects',
    queryFn: (params) => projectAPI.getPublic(params),
    buildParams: buildProjectQueryParams,
    transformResponse: transformProjectResponse,
    filters: filterValues,
    limit: 20,
    maxPages: 10,
    staleTime: 5 * 60 * 1000,
    enabled: resultType === 'projects',
  });

  // ============================================
  // OBSERVATIONS DATA FETCHING
  // ============================================
  const isMapVisible = useMemo(
    () => shouldRenderMobileMap || (!isMobile && viewMode === 'map'),
    [isMobile, shouldRenderMobileMap, viewMode]
  );

  const activeObservationCategory = useMemo(() => {
    const categoryFilters = (filterValues.selectedTags || []).filter(tag => tag !== 'nearby');
    return categoryFilters.length > 0 ? categoryFilters[0] : null;
  }, [filterValues.selectedTags]);

  const buildObservationQueryParams = useCallback((pageParam = 1) => {
    const params = {
      page: pageParam,
      limit: 50,
      layer: 'detections',
      visibility: 'public',
      cluster: true,
    };
    
    if (activeObservationCategory) {
      params.category = activeObservationCategory;
    }
    
    if (filterValues.searchTerm) {
      params.search = filterValues.searchTerm;
    }
    
    // Add bbox if map is visible and synced
    if (mapViewportBbox && isMapVisible && mapUserInteracted && mapSyncEnabled) {
      params.bbox = mapViewportBbox;
    } else if (userLocation && (filterValues.selectedTags || []).includes('nearby')) {
      const radius = 0.5;
      params.bbox = `${userLocation.lng - radius},${userLocation.lat - radius},${userLocation.lng + radius},${userLocation.lat + radius}`;
    }
    
    return params;
  }, [activeObservationCategory, filterValues.searchTerm, filterValues.selectedTags, isMapVisible, mapSyncEnabled, mapUserInteracted, mapViewportBbox, userLocation]);

  const queryKeyBbox = (isMapVisible && mapUserInteracted && mapSyncEnabled) 
    ? mapViewportBbox 
    : ((filterValues.selectedTags || []).includes('nearby') ? userLocation : null);

  const observationsQuery = useInfiniteQuery({
    queryKey: ['discover-observations', filterValues.searchTerm, filterValues.selectedTags, queryKeyBbox, mapUserInteracted, mapSyncEnabled],
    queryFn: async ({ pageParam = 1 }) => {
      const params = buildObservationQueryParams(pageParam);
      const response = await thingsAPI.getItems(params);
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage?.pagination;
      const items = lastPage?.things || [];
      const requestedLimit = 50;
      
      if (items.length === 0) return undefined;
      if (items.length < requestedLimit) return undefined;
      if (pagination && pagination.total <= allPages.length * requestedLimit) return undefined;
      if (allPages.length >= 10) return undefined;
      
      return allPages.length + 1;
    },
    staleTime: 5 * 60 * 1000,
    enabled: resultType === 'observations',
  });

  const observations = useMemo(() => {
    return observationsQuery.data?.pages.reduce((acc, page) => {
      return [...acc, ...(page?.things || [])];
    }, []) || [];
  }, [observationsQuery.data]);

  // ============================================
  // UNIFIED DATA ACCESS
  // ============================================
  const {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = useMemo(() => {
    if (resultType === 'observations') {
      return {
        items: observations,
        isLoading: observationsQuery.isLoading,
        isFetchingNextPage: observationsQuery.isFetchingNextPage,
        hasNextPage: observationsQuery.hasNextPage,
        fetchNextPage: observationsQuery.fetchNextPage,
        refetch: observationsQuery.refetch,
        error: observationsQuery.error,
      };
    }
    return {
      items: projectsData.projects,
      isLoading: projectsData.isLoading,
      isFetchingNextPage: projectsData.isFetchingNextPage,
      hasNextPage: projectsData.hasNextPage,
      fetchNextPage: projectsData.fetchNextPage,
      refetch: projectsData.refetch,
      error: projectsData.error,
    };
  }, [resultType, observations, observationsQuery, projectsData]);

  const itemsForDisplay = useMemo(() => {
    if (mapAreaFilterActive) {
      if (resultType === 'observations') {
        // Observations already have lat/lng at top level
        return items.filter(item => Number.isFinite(item.lat) && Number.isFinite(item.lng));
      }
      return items.filter(projectHasGeolocation);
    }
    return items;
  }, [items, mapAreaFilterActive, resultType]);

  // Map pins for observations
  const observationPins = useMemo(() => {
    if (resultType !== 'observations') return [];
    return observations
      .filter((d) => Number.isFinite(d.lat) && Number.isFinite(d.lng))
      .map((d) => ({
        id: d.id,
        lat: d.lat,
        lng: d.lng,
        name: d.canonical_name || d.label || 'Unknown',
        imageUrl: d.sample_images?.[0] || null,
        count: 1,
        type: d.type || d.category || 'detection',
        _observation: d,
      }));
  }, [resultType, observations]);

  // Refetch when navigating to this page
  useEffect(() => {
    if (location.pathname === '/discover') {
      refetch();
    }
  }, [location.pathname, refetch]);

  // Apply mobile search
  const applyMobileSearch = useCallback(() => {
    handleSearch();
    setIsMobileSearchOpen(false);
  }, [handleSearch, setIsMobileSearchOpen]);

  // Handle tag toggle
  const handleTagToggle = useCallback((tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(t => t !== tagId));
    } else {
      // For observations, allow only one category tag at a time (plus nearby)
      if (resultType === 'observations' && tagId !== 'nearby') {
        setSelectedTags([...selectedTags.filter(t => t === 'nearby'), tagId]);
      } else {
        setSelectedTags([tagId]);
      }
    }
  }, [selectedTags, setSelectedTags, resultType]);

  // Handle nearby toggle
  const handleNearbyToggle = useCallback(() => {
    setSelectedTags((prev) => {
      if (prev.includes('nearby')) {
        return prev.filter((tag) => tag !== 'nearby');
      }
      return [...prev, 'nearby'];
    });
  }, [setSelectedTags]);

  // Filter summary for mobile
  const mobileSearchSummary = useMemo(() => {
    if (searchTerm?.trim()) return searchTerm;
    if (mapAreaFilterActive) return resultType === 'observations' ? 'Observations in map area' : 'Projects in map area';
    return resultType === 'observations' ? 'All Observations' : 'All Public Projects';
  }, [searchTerm, mapAreaFilterActive, resultType]);

  const selectedTagLabels = selectedTags
    .filter((tag) => tag !== 'nearby')
    .map((tag) => allFilterTags.find((t) => t.id === tag)?.label)
    .filter(Boolean);
    
  const mobileFilterSummary = useMemo(() => {
    const parts = [];
    if (selectedTagLabels.length > 0) {
      const preview = selectedTagLabels.slice(0, 2).join(', ');
      const extra = selectedTagLabels.length > 2 ? ` +${selectedTagLabels.length - 2}` : '';
      parts.push(`${preview}${extra}`);
    }
    return parts.length > 0 ? parts.join(' · ') : 'Any filters';
  }, [selectedTagLabels]);

  // Navigation handlers
  const navigateToProject = useCallback((projectId, source = 'unknown') => {
    navigate(`/discover/${projectId}`, {
      state: {
        previousPath: '/discover',
        transitionDirection: 'forward',
      },
    });
  }, [navigate]);

  const navigateToObservation = useCallback((observationId, source = 'unknown') => {
    // Navigate to observation detail
    navigate(`/discover/observation/${observationId}`, {
      state: {
        previousPath: '/discover',
        transitionDirection: 'forward',
        isObservation: true,
      },
    });
  }, [navigate]);

  // Render item (project or observation)
  const renderItem = useCallback((item, index) => {
    if (resultType === 'observations') {
      return (
        <ObservationCard
          key={item.id}
          observation={item}
          onClick={(obs) => navigateToObservation(obs.id, 'card')}
        />
      );
    }
    return (
      <ProjectCard
        key={item.id}
        project={item}
        navigateTo="discover"
        preferCapturedCover={true}
        onProjectClick={(projectId) => navigateToProject(projectId, 'card')}
        isMobile={isMobile}
      />
    );
  }, [resultType, navigateToProject, navigateToObservation, isMobile]);

  // Result type segmented control component
  const ResultTypeSwitcher = useCallback(({ compact = false } = {}) => (
    <div
      className={`inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 p-1 ${
        compact ? 'text-xs' : 'text-sm'
      } font-medium`}
      role="tablist"
      aria-label="Discover result type"
    >
      {RESULT_TYPE_OPTIONS.map((option) => {
        const isActive = resultType === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setResultType(option.value)}
            className={`rounded-full transition-all ${
              compact ? 'px-3 py-1.5' : 'px-4 py-1.5'
            } ${
              isActive
                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  ), [resultType, setResultType]);

  // Render header
  const renderHeader = useCallback(() => {
    const subtitle = resultType === 'observations' 
      ? 'Browse discoveries shared by the community'
      : 'Browse public projects shared by the community';
    
    return (
      <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-between gap-4'}`}>
        <div>
          {!isMobile && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Discover</h1>
                <ResultTypeSwitcher />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
            </div>
          )}
          {isMobile && isFullyExpanded && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-3">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Discover</h1>
                <ResultTypeSwitcher compact={true} />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">{subtitle}</p>
            </div>
          )}
        </div>
        <div className={`flex ${isMobile ? 'flex-col gap-3 w-full' : 'items-center gap-3 flex-wrap justify-end'}`}>
          {!isMobile && (
            <>
              <div className="flex-1 max-w-md">
                <SearchUI
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                  tags={allFilterTags}
                  searchPlaceholder={resultType === 'observations' ? 'Search observations' : 'Search projects'}
                  onSearch={handleSearch}
                  onClearAll={clearAllFilters}
                  className="w-full"
                  mapAreaFilterActive={mapAreaFilterActive}
                  onClearMapAreaFilter={handleClearMapAreaFilter}
                />
              </div>
              
              <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 text-sm font-medium">
                <button
                  onClick={() => handleDesktopViewChange('list')}
                  className={`px-4 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                    viewMode === 'list'
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <Bars3Icon className="w-4 h-4" />
                  List
                </button>
                <button
                  onClick={() => handleDesktopViewChange('map')}
                  className={`px-4 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                    viewMode === 'map'
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  <MapPinIcon className="w-4 h-4" />
                  Map
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }, [
    isMobile,
    isFullyExpanded,
    searchTerm,
    selectedTags,
    viewMode,
    handleSearch,
    clearAllFilters,
    setSearchTerm,
    setSelectedTags,
    handleDesktopViewChange,
    allFilterTags,
    mapAreaFilterActive,
    handleClearMapAreaFilter,
    ResultTypeSwitcher,
    resultType,
  ]);

  // Mobile search overlay using unified component
  const mobileSearchOverlay = (
    <MobileSearchOverlay
      isOpen={isMobile && isMobileSearchOpen}
      onClose={() => setIsMobileSearchOpen(false)}
      title={resultType === 'observations' ? 'Search Observations' : 'Search Discover'}
      searchPlaceholder={resultType === 'observations' ? 'Search observations' : 'Search public projects'}
      mobileSearchTerm={mobileSearchTerm}
      onSearchTermChange={setMobileSearchTerm}
      onSearch={applyMobileSearch}
      selectedTags={selectedTags}
      onTagToggle={handleTagToggle}
      allTags={allFilterTags}
      showSort={false}
      showTags={true}
      mapAreaFilterActive={mapAreaFilterActive}
      onClearMapAreaFilter={handleClearMapAreaFilter}
      onClearAll={clearAllFilters}
      onNearbyToggle={handleNearbyToggle}
    />
  );

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async (invalidateKeys) => {
    const queryKey = resultType === 'observations' ? ['discover-observations'] : ['discover-public-projects'];
    await queryClient.invalidateQueries({ queryKey });
    await refetch(invalidateKeys);
  }, [queryClient, refetch, resultType]);

  // Empty state config based on result type
  const emptyState = useMemo(() => {
    if (resultType === 'observations') {
      return {
        title: 'No observations found',
        message: 'Try adjusting your search or filters',
        icon: MagnifyingGlassIcon,
      };
    }
    return {
      title: 'No public projects yet',
      message: 'Be the first to share a project with the community!',
      icon: GlobeAltIcon,
    };
  }, [resultType]);

  // Render list content using ListPageContent component
  const listContent = (
    <ListPageContent
      projects={itemsForDisplay}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      refetch={refetch}
      error={error}
      renderProject={renderItem}
      isMobile={isMobile}
      isDesktopSplitView={isDesktopSplitView}
      isFullyExpanded={isFullyExpanded}
      onRefresh={handleRefresh}
      invalidateKeys={[[resultType === 'observations' ? 'discover-observations' : 'discover-public-projects']]}
      emptyState={emptyState}
      header={renderHeader()}
      showLoadMoreButton={!isMobile}
    />
  );

  // Render map preview card
  const renderMapPreview = useCallback((item, { onClose }) => {
    if (resultType === 'observations' && item._observation) {
      const obs = item._observation;
      return (
        <div 
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
          onClick={() => {
            navigateToObservation(obs.id, 'map-preview-card');
            onClose();
          }}
        >
          <div className="relative">
            {obs.sample_images?.[0] ? (
              <img
                src={getImageUrl(obs.sample_images[0], 'cardmobile')}
                alt={obs.canonical_name || 'Discovery'}
                className="w-full h-44 object-cover"
              />
            ) : (
              <div className="w-full h-44 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-4xl">
                🔍
              </div>
            )}
          </div>
          
          <div className="p-3">
            <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
              {obs.canonical_name || obs.label || 'Unknown discovery'}
            </h3>
            
            {obs.user_display && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Found by {obs.user_display}
              </p>
            )}
            
            {obs.project_name && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 line-clamp-1">
                in {obs.project_name}
              </p>
            )}
          </div>
        </div>
      );
    }

    // Default: Project preview
    const project = item;
    return (
      <div 
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
        onClick={() => {
          navigateToProject(project.id, 'map-preview-card');
          onClose();
        }}
      >
        <div className="relative">
          {project.images && project.images.length > 0 ? (
            <img
              src={getImageUrl(project.images[0], 'cardmobile')}
              alt={project.name}
              className="w-full h-44 object-cover"
            />
          ) : (
            <div className="w-full h-44 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <MapPinIcon className="w-12 h-12 text-gray-400" />
            </div>
          )}
        </div>
        
        <div className="p-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
              {project.name}
            </h3>
          </div>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
            {project.description || 'No description'}
          </p>
          
          {project.owner && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              by {project.owner.username || project.owner.firstName || 'Anonymous'}
            </p>
          )}
          
          {project.distance && (
            <p className="text-sm text-gray-900 dark:text-white font-medium mt-2">
              {project.distance < 1 
                ? `${Math.round(project.distance * 1000)}m away`
                : `${project.distance.toFixed(1)}km away`
              }
            </p>
          )}
        </div>
      </div>
    );
  }, [resultType, navigateToProject, navigateToObservation]);

  // Determine what to show on the map
  const mapData = useMemo(() => {
    if (resultType === 'observations') {
      return {
        hotspots: observationPins,
        projects: null,
      };
    }
    return {
      hotspots: null,
      projects: itemsForDisplay,
    };
  }, [resultType, observationPins, itemsForDisplay]);

  return (
    <>
      {isMobile && !isMobileSearchOpen && (
        <div
          ref={mobileSearchHeaderRef}
          className="fixed pb-3 px-5 top-0 left-0 right-0 z-[700] pointer-events-auto bg-white dark:bg-gray-900"
          style={{ paddingTop: 'var(--app-safe-area-top)' }}
        >
          <button
            onClick={() => setIsMobileSearchOpen(true)}
            className="mt-3 w-full bg-white/95 dark:bg-gray-700 border border-gray-200 dark:border-gray-600/50 rounded-full px-4 h-[56px] shadow-lg flex items-center gap-3"
          >
            {!searchTerm?.trim() && selectedTags.length === 0 && !mapAreaFilterActive ? (
              <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                <div className="flex items-center justify-center gap-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-black dark:text-gray-300" />
                  {resultType === 'observations' ? 'Search Observations' : 'Search Public Projects'}
                </div>
              </span>
            ) : (
              <div className="flex-1 text-center">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{mobileSearchSummary}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {mapAreaFilterActive
                    ? (mobileFilterSummary !== 'Any filters' ? `Map area · ${mobileFilterSummary}` : 'Filtered by map area')
                    : mobileFilterSummary}
                </p>
              </div>
            )}
          </button>
        </div>
      )}

      <ListMapView
        listContent={listContent}
        projects={mapData.projects}
        mapPins={mapData.hotspots}
        onProjectSelect={(itemId) => {
          if (resultType === 'observations') {
            navigateToObservation(itemId, 'mobile-map');
          } else {
            navigateToProject(itemId, 'mobile-map');
          }
        }}
        onMapPinSelect={(pin) => {
          if (pin._observation) {
            navigateToObservation(pin._observation.id, 'map-pin');
          }
        }}
        onMarkerSelect={(item) => {
          // Selected item is managed by ListMapView internally
        }}
        renderMapPreview={renderMapPreview}
        mapConfig={{
          mode: 'view',
          layoutContext: 'discover',
          autoCenterOnUser: true,
        }}
        hookConfig={{
          enableMap: true,
          treatTabletAsMobile: true,
        }}
        hookState={listMapState}
        onViewportStateChange={handleViewportStateChange}
        emptyStateTitle={emptyState.title}
        emptyStateMessage={emptyState.message}
      />

      {mobileSearchOverlay}
    </>
  );
};

export default DiscoverPage;
